import { CAPTCHA_SITE_KEY } from '@/constants/env';
import { useJobApplyForm } from '@/hooks/job';
import Button from '@/ui/Button';
import FormControl from '@/ui/FormControl';
import FormErrorMessage from '@/ui/FormErrorMessage';
import FormHelperText from '@/ui/FormHelperText';
import FormLabel from '@/ui/FormLabel';
import Input from '@/ui/Input';
import HCaptcha from '@hcaptcha/react-hcaptcha';

export default function JobApplyForm({ lang = 'en', job }) {
  const {
    register,
    errors,
    loading,
    onSubmit,
    captchaRef,
    onCaptchaExpire,
    onCaptchaError,
    onCaptchaVerify,
  } = useJobApplyForm(job);

  const intl = {
    nl: {
      firstName: 'Voornaam',
      lastName: 'Achternaam',
      email: 'Email',
      phone: 'Telefoon',
      motivation: 'Waarom moeten we jou aannemen?',
      resume: 'Curriculum vitae',
      additionalDocuments: 'Aanvullende documenten',
      fieldRequired: 'Dit veld is verplicht',
      buttonSubmit: 'Verstuur',
      buttonSubmitting: 'Versturen...',
    },
    en: {
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      phone: 'Phone',
      motivation: 'Why should we hire you?',
      resume: 'Resume',
      additionalDocuments: 'Additional Documents',
      fieldRequired: 'This field is required',
      buttonSubmit: 'Send',
      buttonSubmitting: 'Sending...',
    },
  }[lang];

  return (
    <form className="flex flex-col gap-4" onSubmit={onSubmit}>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormControl>
          <FormLabel isRequired htmlFor="firstName">
            {intl.firstName}
          </FormLabel>
          <Input
            id="firstName"
            type="text"
            {...register('firstName', {
              required: intl.fieldRequired,
            })}
          />
          <FormErrorMessage>{errors.firstName && errors.firstName.message}</FormErrorMessage>
        </FormControl>
        <FormControl>
          <FormLabel isRequired htmlFor="lastName">
            {intl.lastName}
          </FormLabel>
          <Input
            id="lastName"
            type="text"
            {...register('lastName', {
              required: intl.fieldRequired,
            })}
          />
          <FormErrorMessage>{errors.lastName && errors.lastName.message}</FormErrorMessage>
        </FormControl>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormControl>
          <FormLabel isRequired htmlFor="email">
            {intl.email}
          </FormLabel>
          <Input
            id="email"
            type="email"
            {...register('email', {
              required: intl.fieldRequired,
            })}
          />
          <FormErrorMessage>{errors.email && errors.email.message}</FormErrorMessage>
        </FormControl>
        <FormControl>
          <FormLabel isRequired htmlFor="phone">
            {intl.phone}
          </FormLabel>
          <Input
            id="phone"
            type="text"
            {...register('phone', {
              required: intl.fieldRequired,
            })}
          />
          <FormErrorMessage>{errors.phone && errors.phone.message}</FormErrorMessage>
        </FormControl>
      </div>
      <FormControl>
        <FormLabel isRequired htmlFor="motivation">
          {intl.motivation}
        </FormLabel>
        <Input
          component="textarea"
          id="motivation"
          rows="4"
          {...register('motivation', {
            required: intl.fieldRequired,
          })}
        />
        <FormErrorMessage>{errors.motivation && errors.motivation.message}</FormErrorMessage>
      </FormControl>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormControl>
          <FormLabel isRequired htmlFor="resume">
            {intl.resume}
          </FormLabel>
          <Input
            id="resume"
            type="file"
            {...register('resume', {
              required: intl.fieldRequired,
            })}
          />
          <FormHelperText>(.pdf, .doc, .docx, .txt)</FormHelperText>
          <FormErrorMessage>{errors.resume && errors.resume.message}</FormErrorMessage>
        </FormControl>
        <FormControl>
          <FormLabel htmlFor="additionalDocuments">{intl.additionalDocuments}</FormLabel>
          <Input
            multiple
            id="additionalDocuments"
            type="file"
            {...register('additionalDocuments')}
          />
          <FormHelperText>Maximum 5 documents (.pdf, .doc, .docx, .txt)</FormHelperText>
          <FormErrorMessage>
            {errors.additionalDocuments && errors.additionalDocuments.message}
          </FormErrorMessage>
        </FormControl>
      </div>
      <HCaptcha
        ref={captchaRef}
        sitekey={CAPTCHA_SITE_KEY}
        onError={onCaptchaError}
        onExpire={onCaptchaExpire}
        onVerify={onCaptchaVerify}
      />
      {loading ? (
        <span className="cursor-progress self-start rounded-lg border border-transparent bg-neutral-200 py-2 px-8 uppercase text-neutral-400">
          {intl.buttonSubmitting}
        </span>
      ) : (
        <Button className="self-start px-8" color="primary" type="submit">
          {intl.buttonSubmit}
        </Button>
      )}
    </form>
  );
}
